import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import LegalFooter from "../components/LegalFooter";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message);
      setSubmitted(true);
    } catch (err) {
      // Even on validation errors, fall back to the generic message to avoid enumeration.
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error("Please enter a valid email address.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="forgot-password-screen">
      <header className="px-6 py-5">
        <Link to="/signin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pt-6 pb-16">
        <div className="w-full max-w-md">
          <span className="brand-wordmark text-xs text-muted-foreground">FINALLOOK</span>

          {!submitted ? (
            <>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-3 mb-2">Reset your password</h1>
              <p className="text-sm text-muted-foreground mb-8">
                Enter the email you signed up with. If we find an account, we&apos;ll send a reset link that expires in 30 minutes.
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="h-11"
                    data-testid="forgot-email-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-3" data-testid="forgot-success">
              <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-3">Check your inbox</h1>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6" data-testid="forgot-message">
                {message || "If an account exists for this email, we'll send password reset instructions."}
              </p>
              <p className="text-xs text-muted-foreground mb-8">
                The link expires in 30 minutes. If you don&apos;t see the email, check your spam folder or request another one.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setSubmitted(false); setEmail(""); }}
                  className="rounded-full h-11 border-border font-semibold"
                  data-testid="forgot-resend-btn"
                >
                  Send another link
                </Button>
                <Link to="/signin" className="contents">
                  <Button className="rounded-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold w-full" data-testid="forgot-to-signin-btn">
                    Back to Sign in
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
